"use client";

/**
 * Seller/buyer headline-rating adapter — `GET /api/reviews/profile/{id}`, with
 * a module-level wire dedup so N simultaneous mounts asking about the SAME
 * profile cost one request.
 *
 * **Why the dedup exists.** Nothing in the app mounts this hook once. A single
 * `/listings/[id]` pageview measured FOUR identical
 * `GET /api/reviews/profile/{sellerId}?role=seller&limit=1` on 2026-07-29:
 * two from the feed underneath (`marketplace-vertical-snap-feed` renders a
 * `md:hidden` + a `hidden md:flex` copy of the active card, each with its own
 * `ProfileReviewScoreLink`) and two from the detail view on top
 * (`product-detail-view` renders `MobileProductPage` AND `ProductDetails`
 * simultaneously — `hidden` is `display:none`, React still mounts both — and
 * each renders an `OrderPartyCard`). Snap-scroll inertia multiplies it further:
 * `isActive` toggles false→true through every card the momentum passes over,
 * and each toggle re-ran the effect.
 *
 * The mount count is being attacked separately (triplet handoff Path B); this
 * layer makes the mount count stop mattering at the wire, and it also covers
 * the feed pair, which no layout unification would.
 *
 * **Shape** — the same one already accepted in `listing-suggestions.tsx`
 * (`fetchSuggestionsDedup`): a resolved-value `Map` with a TTL plus an
 * in-flight `Map<userId, Promise>` so concurrent callers await ONE request.
 * Deliberately not an AbortController: the request is shared, so one consumer
 * unmounting must not cancel it for the others. Late resolutions are dropped
 * per-consumer by the `cancelled` flag instead.
 *
 * @see lib/reviews/party-rating.ts — `pickPartyRating`, the canonical seller-then-buyer pick (pure; NOT duplicated here)
 * @see .tondocs/infra/HANDOFF_LISTING_DETAIL_TRIPLET_2026_07_23.md — the mount-count root cause
 * @see tests/unit/use-party-review-stats-dedup.test.ts — pins "two concurrent consumers, one fetch"
 * @see hooks/use-party-followers-count.ts — sibling hook on the same cards, NOT deduped yet
 */

import { useEffect, useState } from "react";
import { pickPartyRating, type PartyRatingSource } from "@/lib/reviews/party-rating";
import type { ReceivedReviewRole } from "@/lib/reviews/schema";
import { unreachable } from "@/lib/type-helpers";

/**
 * The resolved stats, without the loading flag. Canonical shape for handing an
 * already-fetched result down to a child that would otherwise re-run this hook
 * for the same profile — see `ProfileReviewScoreLink`'s `stats` prop.
 */
export type PartyReviewStatsSnapshot = {
  rating: number | null;
  reviewCount: number | null;
  profileReviewRole: ReceivedReviewRole | null;
};

type PartyReviewStats = PartyReviewStatsSnapshot & {
  isLoading: boolean;
};

/**
 * Shared identity for "no rating" — disabled, unresolved, and failed all land
 * here. Passing the SAME reference to `setStats` lets React bail out of the
 * re-render when nothing actually changed.
 */
const EMPTY_SNAPSHOT: PartyReviewStatsSnapshot = {
  rating: null,
  reviewCount: null,
  profileReviewRole: null,
};

/**
 * A request either produced an ANSWER (including the legitimate "this profile
 * has no published reviews" answer, which is cacheable) or FAILED (network
 * error, non-2xx, unparseable body — not cacheable, the next mount should be
 * free to retry). Collapsing both into "empty snapshot" is what would make a
 * transient 500 look like a permanent "no reviews" for the whole TTL.
 */
type ReviewStatsOutcome =
  | { kind: "answered"; snapshot: PartyReviewStatsSnapshot }
  | { kind: "failed" };

/**
 * 60s. Review aggregates only move when someone PUBLISHES a review, so the
 * staleness tolerance is high; what the window has to cover is one user
 * journey — snap-feed scroll past the card, tap through to the listing, browse,
 * hit back — which is exactly where the duplicate requests cluster. A fresh
 * review still surfaces on the next visit a minute later.
 *
 * Longer than the 30s of `fetchSuggestionsDedup` on purpose: that one guards a
 * carousel of listings whose stock/availability genuinely changes underneath
 * the user, this one guards a slow-moving aggregate.
 */
const DEDUP_TTL_MS = 60_000;

const _answered = new Map<string, { snapshot: PartyReviewStatsSnapshot; ts: number }>();
const _inFlight = new Map<string, Promise<ReviewStatsOutcome>>();

/** Cached answer for `userId`, or `null` when absent/expired. */
function readFreshAnswer(userId: string): PartyReviewStatsSnapshot | null {
  const hit = _answered.get(userId);
  if (!hit) return null;
  if (Date.now() - hit.ts >= DEDUP_TTL_MS) {
    _answered.delete(userId);
    return null;
  }
  return hit.snapshot;
}

/**
 * One request per `userId` at a time, shared by every concurrent caller.
 * Never rejects — failure is a value (`kind: "failed"`), so no consumer needs
 * its own `catch` and a rejection can't escape into an unhandled promise.
 */
function fetchReviewStatsDedup(userId: string): Promise<ReviewStatsOutcome> {
  const existing = _inFlight.get(userId);
  if (existing) return existing;

  const request = (async (): Promise<ReviewStatsOutcome> => {
    try {
      const response = await fetch(
        `/api/reviews/profile/${encodeURIComponent(userId)}?role=seller&limit=1`,
      );
      if (!response.ok) return { kind: "failed" };
      const data = (await response.json()) as { stats?: PartyRatingSource };

      // Canonical seller-then-buyer pick — shared with lib/profile-stats.ts
      // (and the wire stats on user rows), so the role fallback cannot drift.
      const picked = pickPartyRating(data.stats);
      const snapshot: PartyReviewStatsSnapshot = {
        rating: picked.rating,
        reviewCount: picked.reviewCount,
        profileReviewRole: picked.role,
      };
      _answered.set(userId, { snapshot, ts: Date.now() });
      return { kind: "answered", snapshot };
    } catch {
      return { kind: "failed" };
    }
  })();

  // `.finally()` rather than a `finally {}` block inside the async body: the
  // body runs synchronously up to the first `await`, so a synchronous throw
  // from `fetch` would clear the entry BEFORE the `set` below and strand a
  // never-deleted promise in the map. A `.finally()` callback is always a
  // microtask, i.e. always after the `set`.
  const tracked = request.finally(() => {
    _inFlight.delete(userId);
  });
  _inFlight.set(userId, tracked);
  return tracked;
}

export function usePartyReviewStats(
  userId: string | null | undefined,
  enabled: boolean,
): PartyReviewStats {
  const [stats, setStats] = useState<PartyReviewStatsSnapshot>(EMPTY_SNAPSHOT);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !userId) {
      setStats(EMPTY_SNAPSHOT);
      setIsLoading(false);
      return;
    }

    // Re-enable (feed card scrolled back into view, dialog reopened) serves the
    // cache synchronously inside the effect — no request, and no flash of
    // "loading"/empty between the toggle and the next microtask.
    const cached = readFreshAnswer(userId);
    if (cached) {
      setStats(cached);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    void fetchReviewStatsDedup(userId).then((outcome) => {
      // Only THIS consumer is cancelled; the shared request keeps running for
      // whoever else is still mounted.
      if (cancelled) return;
      switch (outcome.kind) {
        case "answered":
          setStats(outcome.snapshot);
          break;
        case "failed":
          setStats(EMPTY_SNAPSHOT);
          break;
        default:
          unreachable(outcome);
      }
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, userId]);

  return { ...stats, isLoading };
}
